import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { rerunAsChild, retryRunStage } from '../services/recovery.service';
import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { RunRead } from '../types/run.api.types';

/**
 * How a batch is retried from the list. `stage` re-opens the stage it stopped at in place
 * (POST …/retry-stage, earlier work kept); `over` reprocesses every product as a new child run
 * (POST …/rerun, scope ALL).
 */
export type BatchRetryMode = 'stage' | 'over';

/** Variables for one list-level retry. */
export interface BatchRetryVariables {
  runIds: string[];
  mode: BatchRetryMode;
  /** Optional operator note, sent as the control's `reason`. */
  note?: string;
}

/** Per-run outcome of a (bulk) retry — the list never fails as a whole. */
export interface BatchRetryResult {
  succeeded: string[];
  failed: { runId: string; error: unknown }[];
}

/** Prefix shared by every runs-list page cache — the optimistic update target. */
const LIST_PREFIX = [...queryKeys.catalogService.runs.all(), 'list'] as const;

/** Fires one retry control for one run, with a fresh correlation id per call. */
async function retryOne(runId: string, mode: BatchRetryMode, note: string | undefined) {
  const correlationId = crypto.randomUUID();
  const reason = note?.trim() ? { reason: note.trim() } : {};
  if (mode === 'stage') {
    await retryRunStage(runId, { correlationId, acknowledgeUnconfirmed: false, ...reason });
  } else {
    await rerunAsChild(runId, { scope: 'ALL', correlationId, ingestMode: 'all', ...reason });
  }
}

/**
 * Retries one or many batches from the Batches list (row, bulk bar, detail panel, Fix & retry).
 * Rows retried in place flip to RUNNING optimistically, then every runs read is invalidated so
 * the server's answer wins. Calls are sent one by one and never auto-retried (a resend with a
 * fresh correlation id could act twice); each run's outcome is reported separately.
 */
export function useBatchRetry() {
  const queryClient = useQueryClient();
  return useMutation<BatchRetryResult, Error, BatchRetryVariables>({
    mutationFn: async ({ runIds, mode, note }) => {
      const result: BatchRetryResult = { succeeded: [], failed: [] };
      for (const runId of runIds) {
        try {
          await retryOne(runId, mode, note);
          result.succeeded.push(runId);
        } catch (error) {
          result.failed.push({ runId, error });
        }
      }
      return result;
    },
    onMutate: async ({ runIds, mode }) => {
      // Only an in-place retry moves THIS row; "start over" spawns a child run instead.
      if (mode !== 'stage') return;
      await queryClient.cancelQueries({ queryKey: LIST_PREFIX });
      const ids = new Set(runIds);
      queryClient.setQueriesData<CatalogPage<RunRead>>({ queryKey: LIST_PREFIX }, (page) =>
        page
          ? {
              ...page,
              items: page.items.map((run) =>
                ids.has(run.id) ? { ...run, status: 'RUNNING', listStatus: 'RUNNING' } : run,
              ),
            }
          : page,
      );
    },
    onSettled: async (_data, _error, { runIds }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.runs.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.schedules.all() }),
        ...runIds.map((runId) =>
          queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.run(runId) }),
        ),
      ]);
    },
  });
}
